import { Button, Icon, Popover } from '@patternfly/react-core';
import {
  AngleDownIcon,
  AngleRightIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon,
} from '@patternfly/react-icons';
import ComponentError from 'components/Error/ComponentError';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { handleError } from 'services/ErrorService';
import { UserEntitlement } from 'types/user';
import './styles.scss';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from 'hooks/useMobxStores';

const SubscriptionsTable = () => {
  const { t } = useTranslation();
  const { userStore, routerStore } = useMobxStores() as any;
  const { enforceLabLimit, entitlementEnablements } = userStore;
  const { sortedEntitlements } = userStore as {
    sortedEntitlements: UserEntitlement[];
  };

  const { route } = routerStore;

  const isALCCourse =
    userStore.isLoggedIn &&
    (userStore.isExternalSubscriber ||
      userStore.isAlacarteSubscriberForCourse(route?.params?.course)) &&
    route.name.includes('courses');

  const [expanded, setExpanded] = useState(false);

  const exclamationIcon = (
    <React.Fragment>
      <Icon status="warning" size="lg" className="exclamation-triangle-icon">
        <ExclamationTriangleIcon />
      </Icon>
    </React.Fragment>
  );

  const infoCircleIcon = (
    <React.Fragment>
      <Icon status="info" size="lg" className="exclamation-triangle-icon">
        <InfoCircleIcon />
      </Icon>
    </React.Fragment>
  );

  const getLabUsageRemaining = (
    used: number = 0,
    allowed: number = 0,
  ): React.ReactElement => {
    const hours = Math.max(allowed - used, 0);

    const labHoursRemainingTxt = <>{t('Lab hours remaining')}:&nbsp;</>;

    if (hours > 20 || !enforceLabLimit) {
      return (
        <>
          {t('Lab hours used')} &nbsp;
          <strong>
            {used}/{allowed}
          </strong>
        </>
      );
    }

    if (hours > 10) {
      return (
        <>
          {infoCircleIcon} {labHoursRemainingTxt}
          <span className="usage-info-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    if (hours > 0) {
      return (
        <>
          {exclamationIcon} {labHoursRemainingTxt}
          <span className="usage-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    return (
      <div className="usage-alert-bg">
        <span className="usage-alert">
          <strong>{t('All lab hours have been used')}</strong>
        </span>
      </div>
    );
  };

  const getRemainingEntitlementTime = (endTime: string): number =>
    DateTime.fromISO(endTime).diff(DateTime.now(), 'days').days;

  return (
    sortedEntitlements.length !== 0 && (
      <div className="subscriptions-table-container">
        <Button
          variant="plain"
          className="my-subscriptions-button"
          onClick={() => setExpanded(!expanded)}
          isActive
        >
          <strong>{t('My subscriptions')}</strong>
          <Icon size="lg">
            {expanded ? <AngleDownIcon /> : <AngleRightIcon />}
          </Icon>
        </Button>
        <div
          className={`subscriptions-table ${
            expanded ? 'subscriptions-table__expanded' : ''
          }`}
        >
          {!isALCCourse && sortedEntitlements.length > 1 && (
            <div className="subscriptions-table-enrollment-section">
              <div className="subscriptions-table-usage-summary">
                <strong className="subscriptions-table-usage-summary-tag">
                  {t('Usage summary')}
                </strong>
                <Popover
                  bodyContent={t(
                    'Displays your combined usage across all active purchases. Metrics are aggregated where applicable, such as lab hours and exams, and displayed as a single total',
                  )}
                  aria-label={t('Usage summary information')}
                >
                  <Icon status="info">
                    <InfoCircleIcon />
                  </Icon>
                </Popover>
              </div>
              {entitlementEnablements.quotas?.lab_hours?.allowed > 0 && (
                <div>
                  {getLabUsageRemaining(
                    entitlementEnablements.quotas.lab_hours.used,
                    entitlementEnablements.quotas.lab_hours.allowed,
                  )}
                </div>
              )}
              {entitlementEnablements.quotas?.exams?.allowed > 0 && (
                <div>
                  <span>{t('Exams used')}</span>
                  <strong>
                    {entitlementEnablements.quotas.exams.used}/
                    {entitlementEnablements.quotas.exams.allowed}
                  </strong>
                </div>
              )}
              {entitlementEnablements.quotas?.experiences?.allowed > 0 && (
                <div>
                  <span>{t('Exam experiences used')}</span>
                  <strong>
                    {entitlementEnablements.quotas.experiences.used}/
                    {entitlementEnablements.quotas.experiences.allowed}
                  </strong>
                </div>
              )}
            </div>
          )}
          {sortedEntitlements.map(
            (entitlement) =>
              (!isALCCourse ||
                extractFromSlug(route?.params?.course).code ===
                  extractFromSlug(entitlement.entitlement_info.offering_slug)
                    .code) && (
                <div className="subscriptions-table-enrollment-section">
                  <strong className="subscriptions-table-section-title-tag">
                    {entitlement.entitlement_info.title}
                  </strong>
                  <div>
                    <span>{t('Days remaining')}</span>
                    <strong>
                      {Math.trunc(
                        getRemainingEntitlementTime(
                          entitlement.entitlement_info.end_time,
                        ),
                      )}
                    </strong>
                  </div>

                  {entitlement.quotas?.lab_hours?.allowed > 0 && (
                    <div>
                      {getLabUsageRemaining(
                        entitlement.quotas.lab_hours.used,
                        entitlement.quotas.lab_hours.allowed,
                      )}
                    </div>
                  )}
                  {entitlement.quotas?.exams?.allowed > 0 && (
                    <div>
                      <span>{t('Exams used')}</span>
                      <strong>
                        {entitlement.quotas.exams.used}/
                        {entitlement.quotas.exams.allowed}
                      </strong>
                    </div>
                  )}
                  {entitlement.quotas?.experiences?.allowed > 0 && (
                    <div>
                      <span>{t('Exam experiences used')}</span>
                      <strong>
                        {entitlement.quotas.experiences.used}/
                        {entitlement.quotas.experiences.allowed}
                      </strong>
                    </div>
                  )}
                  {entitlement.quotas?.live_class_enrolled?.allowed > 0 && (
                    <div>
                      <span>{t('Live classes enrolled')}</span>
                      <strong>
                        {entitlement.quotas.live_class_enrolled.used}/
                        {entitlement.quotas.live_class_enrolled.allowed}
                      </strong>
                    </div>
                  )}
                </div>
              ),
          )}
        </div>
      </div>
    )
  );
};

export default withErrorBoundary(
  observer(SubscriptionsTable),
  ComponentError,
  handleError,
);
